import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { map, switchMap } from 'rxjs';

import { MeService } from '../services/me.service';
import { NavigationService } from '../services/navigation.service';
import { PermissionsService } from '../services/permissions.service';
import { TenantService } from '../services/tenant.service';

/**
 * Blocks navigation to a child route when the user lacks the permission declared in its
 * `data.permission`. Routes without a `permission` are always allowed. This is defense-in-depth
 * for the UI — the API still enforces permissions on every data endpoint.
 */
export const permissionChildGuard: CanActivateChildFn = (childRoute, state) => {
  const required = childRoute.data?.['permission'] as string | undefined;
  if (!required) {
    return true;
  }

  const permissions = inject(PermissionsService);
  const navigation = inject(NavigationService);
  const meService = inject(MeService);
  const tenantService = inject(TenantService);
  const router = inject(Router);

  // Deep links can run this guard before AppLayout initializes. Resolve /api/me and
  // its valid tenant first so permission requests always carry the right tenant.
  return meService.ensureLoaded().pipe(
    switchMap(() => {
      const me = meService.me();
      const tenantChanged = tenantService.ensureValidTenant(
        me?.tenants.map(tenant => tenant.tenantKey) ?? [],
        me?.defaultTenantKey
      );

      if (tenantChanged) {
        permissions.clear();
        navigation.clear();
      }

      return permissions.ensureLoaded();
    }),
    map(() => (permissions.has(required) || navigation.hasRoute(state.url) ? true : router.createUrlTree(['/unauthorized'])))
  );
};
